/**
 * @brisa/ui · facelift/pin-map/PinMap
 *
 * Feature: migracao-facelift-v1
 * Figma origem: node 194:3943 — frame /clubes, terceiro layout (mapa ativo).
 *
 * Função: marker visual posicionado pelo Mapbox sobre cada coordenada de club
 * na variant /clubes?view=mapa. NÃO confundir com `ClubMapMarkerMiniCard`
 * (popover companion que abre ao clicar num pin — vive em `/club-map-marker/`).
 *
 * Semântica visual (per Bruno 2026-05-16):
 *   - `integrationStatus=partner`  → outer ring AMARELO (--accent-yellow)
 *   - `integrationStatus=listed`   → outer ring neutro (--text-32)
 *   - `state=selected`             → mesma cor do ring (partner/listed) mas
 *                                    com 2px de border SOLID ADICIONAL ao redor
 *                                    do outer ring (anel duplo de seleção).
 *   - Hover: scale-110 (CSS-only, sem variante de design).
 *
 * Anatomia (per Figma):
 *   - Container 24×24 (md) — 3 ellipses concêntricas absolutamente posicionadas
 *   - outer:   24×24 — cor por status (partner=amarelo / listed=neutro)
 *   - middle:  20×20 — neutral-white (anel branco constante)
 *   - inner:   12×12 — text-100 (dot preto sólido)
 *   - Quando selected: outer ganha `outline` 2px da cor do ring (ou ring extra)
 *
 * Decisões técnicas:
 *   - SEM 'use client' — componente puramente visual, sem state interno.
 *   - `<button>` — semântica nativa (foco, Enter/Space, aria-label, onClick).
 *   - SEM framer-motion — performance crítica em mapas com 50+ pins.
 */
import * as React from 'react';

import { cn } from '@brisa/ui/lib/utils';

export type PinMapState = 'default' | 'selected';
export type PinMapSize = 'sm' | 'md';
export type PinMapIntegrationStatus = 'partner' | 'listed';

export interface PinMapProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Nome do club que este pin representa — usado em aria-label default. */
  clubName: string;
  /** Status do club. `partner` → ring amarelo · `listed` → ring neutro. */
  integrationStatus?: PinMapIntegrationStatus;
  /** Estado visual. `default` ou `selected` (mesma cor do ring + 2px outline). */
  state?: PinMapState;
  /** Tamanho do pin. `md` (24px, default do Figma) ou `sm` (16px, p/ clusters). */
  size?: PinMapSize;
}

const SIZE_PX: Record<PinMapSize, { outer: number; middle: number; inner: number }> = {
  sm: { outer: 16, middle: 14, inner: 8 },
  md: { outer: 24, middle: 20, inner: 12 },
};

export const PinMap = React.forwardRef<HTMLButtonElement, PinMapProps>(function PinMap(
  {
    clubName,
    integrationStatus = 'listed',
    state = 'default',
    size = 'md',
    className,
    'aria-label': ariaLabelProp,
    ...props
  },
  ref,
) {
  const dims = SIZE_PX[size];
  const isSelected = state === 'selected';
  const isPartner = integrationStatus === 'partner';
  const ariaLabel = ariaLabelProp ?? `Ver ${clubName} no mapa`;

  return (
    <button
      ref={ref}
      type="button"
      aria-label={ariaLabel}
      aria-pressed={isSelected || undefined}
      data-slot="pin-map"
      data-state={state}
      data-integration-status={integrationStatus}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center justify-center bg-transparent p-0',
        'transition-transform duration-200 ease-out will-change-transform',
        'hover:scale-110',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2',
        'rounded-full',
        className,
      )}
      style={{ width: dims.outer, height: dims.outer }}
      {...props}
    >
      {/* Outer ring — cor por integrationStatus.
          partner → accent-yellow · listed → text-32 (neutro).
          Selected adiciona outline 2px da mesma cor (anel duplo). */}
      <span
        aria-hidden="true"
        data-slot="pin-ring-outer"
        className={cn(
          'absolute inset-0 rounded-full',
          isPartner ? 'bg-[hsl(var(--accent-yellow))]' : 'bg-[hsl(var(--text-32))]',
          isSelected && 'ring-2 ring-offset-1',
          isSelected && (isPartner
            ? 'ring-[hsl(var(--accent-yellow))]'
            : 'ring-[hsl(var(--text-32))]'),
        )}
      />

      {/* Middle ring — branco constante */}
      <span
        aria-hidden="true"
        data-slot="pin-ring-middle"
        className="absolute rounded-full bg-[hsl(var(--neutral-white))]"
        style={{ width: dims.middle, height: dims.middle }}
      />

      {/* Inner dot — preto constante */}
      <span
        aria-hidden="true"
        data-slot="pin-dot-inner"
        className="absolute rounded-full bg-[hsl(var(--text-100))]"
        style={{ width: dims.inner, height: dims.inner }}
      />
    </button>
  );
});

PinMap.displayName = 'PinMap';
